/**
 * Authentication Context and Hook for Landlord Portal
 * Provides authentication state and methods throughout the app
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  type ReactNode,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  managerSession,
  managerLogout,
  landlordFeatureToggles,
  onSessionExpired,
  type Manager,
  type Portfolio,
  type ManagerSession,
} from '../lib/api';
import { createLogger } from '@resida/shared';
import { isSessionProbeExemptPath } from '../lib/session-probe';
import { usePushNotifications } from './usePushNotifications';

const logger = createLogger('landlord:hooks:useAuth');

// Auth context state
interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  manager: Manager | null;
  portfolios: Portfolio[];
  // Currently-resolved enabled feature toggle keys (BR-10/US-9) — the one interface any
  // future toggle-gated UI in this app reads. Populated best-effort in checkSession();
  // defaults to [] rather than failing the session check on error.
  enabledToggleKeys: string[];
  // True once the session outcome is known (probe finished, or login/logout set it). Until then,
  // a route that needs the session reports isLoading; a probe-exempt route does not.
  sessionKnown: boolean;
}

// Auth context methods
interface AuthContextValue extends AuthState {
  login: () => void;
  logout: () => Promise<void>;
  checkSession: () => Promise<void>;
  refreshSession: () => Promise<void>;
  setSession: (session: ManagerSession) => void;
}

// Create context with undefined default
const AuthContext = createContext<AuthContextValue | undefined>(undefined);

// Provider props
interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Auth Provider Component
 * Wraps the app and provides authentication state
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const [state, setState] = useState<AuthState>({
    isAuthenticated: false,
    // Derived while the session is unknown — see `isLoading` in the context value below.
    isLoading: false,
    manager: null,
    portfolios: [],
    enabledToggleKeys: [],
    sessionKnown: false,
  });

  const navigate = useNavigate();
  const { pathname } = useLocation();
  // Public entry routes (login, magic-link verify, the signing ceremony) never need the session, so
  // they don't probe it — an anonymous visitor would only collect expected 401s. See session-probe.ts.
  const probeExempt = isSessionProbeExemptPath(pathname);

  // Push notification deregistration — must be called BEFORE clearing the session
  const { deregister: deregisterPushNotifications } = usePushNotifications();
  const deregisterRef = useRef(deregisterPushNotifications);
  deregisterRef.current = deregisterPushNotifications;

  /**
   * Check current session status
   */
  const checkSession = useCallback(async () => {
    setState((prev) => ({ ...prev, isLoading: true }));

    try {
      const session = await managerSession();

      if (session) {
        // Best-effort: a failure to resolve enabled feature toggles must never fail the
        // session check itself — log and default to [] (BR-10/US-9).
        let enabledToggleKeys: string[] = [];
        try {
          enabledToggleKeys = (await landlordFeatureToggles()).enabledKeys;
        } catch (toggleError) {
          logger.error('Failed to load feature toggles:', toggleError);
        }

        setState({
          isAuthenticated: true,
          isLoading: false,
          manager: {
            id: session.identity.id,
            email: session.identity.email,
            displayName: session.identity.displayName,
            createdAt: '', // Session doesn't include this
          },
          portfolios: session.portfolios,
          enabledToggleKeys,
          sessionKnown: true,
        });
      } else {
        setState({
          isAuthenticated: false,
          isLoading: false,
          manager: null,
          portfolios: [],
          enabledToggleKeys: [],
          sessionKnown: true,
        });
      }
    } catch (error) {
      logger.error('Failed to check session:', error);
      setState({
        isAuthenticated: false,
        isLoading: false,
        manager: null,
        portfolios: [],
        enabledToggleKeys: [],
        sessionKnown: true,
      });
    }
  }, []);

  /**
   * Refresh session - alias for checkSession
   */
  const refreshSession = useCallback(async () => {
    await checkSession();
  }, [checkSession]);

  /**
   * Set session after successful login/register
   */
  const setSession = useCallback((session: ManagerSession) => {
    setState((prev) => ({
      isAuthenticated: true,
      isLoading: false,
      manager: {
        id: session.identity.id,
        email: session.identity.email,
        displayName: session.identity.displayName,
        createdAt: '',
      },
      portfolios: session.portfolios,
      // Not resolved by the login/register flow itself — preserved from prior state (a
      // subsequent checkSession()/refreshSession() call is what populates it, per BR-10/US-9).
      enabledToggleKeys: prev.enabledToggleKeys,
      sessionKnown: true,
    }));
  }, []);

  /**
   * Redirect to login page
   */
  const login = useCallback(() => {
    navigate('/login');
  }, [navigate]);

  /**
   * Logout and clear session
   * Deregisters push notifications BEFORE clearing the session so the
   * DELETE /devices/:id call has a valid auth token.
   */
  const logout = useCallback(async () => {
    // Deregister device first (best-effort — never blocks logout)
    await deregisterRef.current();

    try {
      await managerLogout();
    } catch (error) {
      logger.error('Logout error:', error);
      // Continue with local logout even if API fails
    }

    setState({
      isAuthenticated: false,
      isLoading: false,
      manager: null,
      portfolios: [],
      enabledToggleKeys: [],
      sessionKnown: true,
    });

    navigate('/login');
  }, [navigate]);

  // Check session on mount — except on a probe-exempt public route. Navigating from such a route
  // to a protected one changes `probeExempt`, re-runs this effect and probes then; until that
  // finishes `isLoading` (below) is true, so ProtectedRoute shows its spinner rather than
  // redirecting a signed-in user to /login.
  const sessionKnownRef = useRef(false);
  sessionKnownRef.current = state.sessionKnown;
  useEffect(() => {
    if (probeExempt || sessionKnownRef.current) return;
    void checkSession();
  }, [checkSession, probeExempt]);

  // Listen for session expiry events from API client
  // Only redirect if we were previously authenticated
  useEffect(() => {
    const unsubscribe = onSessionExpired(() => {
      // Only redirect if we're currently authenticated
      // This prevents redirect loops during login/register flows
      if (state.isAuthenticated) {
        logger.warn('Session expired, redirecting to login');
        setState({
          isAuthenticated: false,
          isLoading: false,
          manager: null,
          portfolios: [],
          enabledToggleKeys: [],
          sessionKnown: true,
        });
        navigate('/login');
      }
    });

    return () => unsubscribe();
  }, [navigate, state.isAuthenticated]);

  const value: AuthContextValue = {
    ...state,
    isLoading: state.isLoading || (!state.sessionKnown && !probeExempt),
    login,
    logout,
    checkSession,
    refreshSession,
    setSession,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Hook to access auth context
 * Must be used within AuthProvider
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}
